import { bind, classNames, h, signal, tag } from "omi";

import { UrlValidation } from "@/components/validations/models.ts";
import { isValidURL } from "@/utils/url";
import { ValidationStatus, ValidationType } from "@/api";
import { NWIcon } from "@/components";
import { tailwind } from "@/tailwind";

import { EditAnswer } from "../common/editAnswer";
import baseStyles from "../baseStyles.css?inline";
import { ValidationBase } from "../validationBase";

export type Props = {
  title: string;
  description: string;
  helpText?: string;
  extension?: string;
  disabled?: boolean;
  error?: string;
  optional?: boolean;
};

@tag("nw-validation-url")
export class NWValidationUrl extends ValidationBase<UrlValidation, Props> {
  static css = [tailwind, baseStyles];

  static propTypes = {
    title: String,
    description: String,
    helpText: String,
    disabled: Boolean,
    error: String,
    extension: String,
    optional: Boolean,
  };

  minimumToBeFilled = 3;
  text = signal<string>("");

  private extension = signal<string | undefined>(undefined);
  private processError = signal<string | undefined>(undefined);

  install() {
    super.install();
    this.extension.value = this.props.extension || "https://";
  }

  @bind
  private handleOnChange(event: Event) {
    const oldText = this.text.value;
    const eventValue = (event.target as HTMLInputElement).value;

    this.text.value = eventValue;

    // // Update the editor content
    // if (this.editor) {
    //   this.editor.commands.command(({ tr, dispatch }) => {
    //     if (dispatch) {
    //       const pos = (this.editor?.state.selection.$anchor.pos ?? 1) - 1;
    //       const node = this.editor?.state.doc.nodeAt(pos);
    //       if (node && node.type.name === 'nwValidationUrl') {
    //         tr.setNodeMarkup(pos, undefined, {
    //           ...node.attrs,
    //           data: JSON.stringify({ text: { data: eventValue } })
    //         });
    //       }
    //     }
    //     return true;
    //   });
    // }

    // Handle clearing the validation when all data is removed.
    if (oldText.trim().length > this.minimumToBeFilled && eventValue.trim().length === 0) {
      this.save(true);
    }
  }

  @bind
  isFilled(): boolean {
    return this.text.value.trim().length >= this.minimumToBeFilled;
  }

  @bind
  async processInternal(): Promise<ValidationStatus> {
    if (!this.isFilled()) {
      return ValidationStatus.Incomplete;
    }

    const res = isValidURL(this.text.value);
    this.processError.value = res ? "" : "Not a valid URL";

    if (res) {
      return ValidationStatus.Complete;
    }

    return ValidationStatus.Incorrect;
  }

  @bind
  public hydrate(validation: UrlValidation | undefined): void {
    this.text.value = validation?.text?.data || this.text.value;
    this.status.value = validation?.status || this.status.value;
  }

  toDTO(): UrlValidation {
    return {
      id: this.id,
      status: this.status.value,
      type: ValidationType.Url,
      text: {
        data: this.text.value,
      },
    };
  }

  @bind
  private renderSubtext() {
    const isCompleted = this.status.value === ValidationStatus.Complete;
    const hasError = !!this.props.error && this.props.error.length > 0;

    return (
      <div class="inline-flex items-center space-x-2">
        <label className={classNames("text-sm mt-1.5", { "text-error-600": hasError, "text-tertiary": !hasError })}>
          {isCompleted && <EditAnswer />}
          {!isCompleted && (hasError ? this.props.error : this.props.helpText)}
        </label>
      </div>
    );
  }

  render({ title, description, error, disabled }: Props) {
    const hasError = this.processError.value ? this.processError.value : !!error && error.length > 0;
    const isCompleted = this.status.value === ValidationStatus.Complete;

    return (
      <div className="">
        <div className="space-y-4 mb-10">
          <h3 className="font-bold text-2xl text-primary">{title}</h3>
          <p unsafeHTML={{ html: description }}></p>
        </div>

        <div className="flex w-full items-center h-10 bg-white shadow-xs">
          <p
            className={classNames(
              "border border-e-0 flex-shrink-0 z-10 text-tertiary select-none border-gray-300 inline-flex items-center py-2 px-4 rounded-s-lg",
              { "bg-gray-50": disabled || isCompleted },
            )}
          >
            {this.extension.value}
          </p>
          <div className="relative inline-flex w-full">
            <input
              disabled={disabled || isCompleted}
              className={classNames(
                "py-2 pl-4 pr-9 w-full z-20 rounded-e-lg outline-none transition-all border",
                {
                  "border-error-300 focus:shadow-xse focus:border-error-600": hasError,
                  "border-gray-300 focus:shadow-xss focus:border-brand-400": !hasError,
                },
                { "bg-gray-50": disabled },
              )}
              placeholder="Go on, we know you have a great answer.."
              maxLength="2048"
              tabIndex={1}
              onInputCapture={this.handleOnChange}
              value={this.text.value}
            />
            {hasError && (
              <NWIcon name="alert-circle" className="absolute right-3 top-1/2 transform -translate-y-1/2 z-50" />
            )}
          </div>
        </div>
        <div className="">{this.renderSubtext()}</div>
      </div>
    );
  }
}
